import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react'

type SelectedSubTradesContextValue = {
  selectedSubTradeIds: string[]
  setSelectedSubTradeIds: (ids: string[]) => void
}

const SelectedSubTradesContext = createContext<SelectedSubTradesContextValue | null>(null)

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i])

export function SelectedSubTradesProvider({ children }: { children: ReactNode }) {
  const [selectedSubTradeIds, setIds] = useState<string[]>([])
  // Dedupe writes so a fresh-but-equal array from a caller's derived
  // computation doesn't churn every consumer in the Provider subtree.
  const setSelectedSubTradeIds = useCallback((ids: string[]) => {
    setIds((prev) => (sameIds(prev, ids) ? prev : ids))
  }, [])
  const value = useMemo(
    () => ({ selectedSubTradeIds, setSelectedSubTradeIds }),
    [selectedSubTradeIds, setSelectedSubTradeIds],
  )
  return (
    <SelectedSubTradesContext.Provider value={value}>
      {children}
    </SelectedSubTradesContext.Provider>
  )
}

// Safe outside the Provider — returns empty selection + no-op setter so
// consumers don't have to null-check.
export function useSelectedSubTrades(): SelectedSubTradesContextValue {
  return (
    useContext(SelectedSubTradesContext) ?? {
      selectedSubTradeIds: [],
      setSelectedSubTradeIds: () => {},
    }
  )
}
